import React, { useEffect, useRef, useState } from 'react';
import { API_URLS } from '../apiUrl';
import PropertyTypeDropdown from '../pages/customHooks/propertyTypeDropdown';
import axiosInstance from '../components/axiosInstance';

const PropertyTypeDescriptionEdit = ({ isOpen, closeModal, typeDescriptionName, typeDescriptionId, propertyTypeId, propertyTypeName }: any) => {
    
    const [handleDropdownToggle, isDropdownOpen, propertyTypes, loading, setIsDropdownOpen] = PropertyTypeDropdown();

    const [formData, setFormData] = useState<any>({
        type_description_name: "",
        property_type_id: "",
        property_type_description_id: typeDescriptionId,
    });

    const [errors, setErrors] = useState<any>({});
    const [successMessage, setSuccessMessage] = useState<string>("");
    const [dataExistMessage, setDataExistMessage] = useState<string>("");

    const dropdownRef = useRef<any>(null);

    // Update form when modal opens
    useEffect(() => {
        if (isOpen) {
            setFormData({
                type_description_name: typeDescriptionName || "",
                property_type_id: propertyTypeId || "",
                property_type_description_id: typeDescriptionId,
            });
            setErrors({});
        }
    }, [isOpen, typeDescriptionName, propertyTypeId, typeDescriptionId]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, [setIsDropdownOpen]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev: any) => ({
            ...prev,
            [name]: value,
        }));
        // Clear error when user starts typing
        if (errors.description_name) {
            setErrors((prev: any) => ({ ...prev, description_name: "" }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: any = {};
        const trimmedName = formData.type_description_name?.trim();
    
        if (!trimmedName) {
            newErrors.description_name = "Description name is required";
        } else if (!/^[a-zA-Z\s]+$/.test(trimmedName)) {
            newErrors.description_name = "Description name can only contain text and spaces";
        }
    
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handlePropertyTypeSelect = (selectedValue: string) => {
        const selectedProperty = propertyTypes.find(
            (property: any) => property.type_primary_id === parseInt(selectedValue)
        );

        setFormData((prev: any) => ({
            ...prev,
            property_type_id: selectedValue,
        }));

        setIsDropdownOpen(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) return;

        try {
            const response = await axiosInstance.put(`${API_URLS.PropertyTypeDescriptionUpdate}`, {
                property_type_id: formData.property_type_id,
                type_description_name: formData.type_description_name.trim(),
                property_type_description_id: typeDescriptionId
            });
           
            if (response.data.message) {
                setSuccessMessage(response.data.message);
                setTimeout(() => {
                    setSuccessMessage("");
                    closeModal();
                }, 5000);
            }
            setErrors({});
        } catch (error: any) {
            const errorMsg = error?.response?.data?.message || "Type Description Name already exists. Please choose a different name.";
            setDataExistMessage(errorMsg);
            setTimeout(() => {
                setDataExistMessage("");
                closeModal();
            }, 5000);
        }
    };

    const selectedPropertyTypeName = propertyTypes.find(
        (property: any) => property.type_primary_id === parseInt(formData.property_type_id)
    )?.type_name || propertyTypeName;

    if (!isOpen) return null;

    const handleOutsideClick = (e: any) => {
        if (e.target === e.currentTarget) {
            closeModal();
        }
    };

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center" onClick={handleOutsideClick}>
            <div className="bg-white p-6 rounded-lg shadow-lg w-[740px] relative" onClick={(e) => e.stopPropagation()}>
                <h2 className="text-xl font-semibold mb-4">Edit Property Type Description</h2>

                <form onSubmit={handleSubmit}>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {/* Property Type Dropdown */}
                        <div>
                            <label htmlFor="property_type_id" className="block mb-2 text-sm font-medium text-gray-900">
                                Property Type <span className="text-red-600">*</span>
                            </label>
                            <div className="relative" ref={dropdownRef}>
                                <button 
                                    type="button"
                                    onClick={handleDropdownToggle} 
                                    className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg w-full p-2.5 pr-3 text-left flex items-center justify-between"
                                >
                                    {selectedPropertyTypeName || "Select Property Type"}
                                    <i className="ri-arrow-down-s-line ri-lg"></i>
                                </button>
                                
                                {isDropdownOpen && (
                                    <select
                                        value={formData.property_type_id}
                                        onChange={(e) => handlePropertyTypeSelect(e.target.value)}
                                        className="absolute top-full left-0 w-full bg-gray-50 border border-gray-300 text-sm rounded-lg focus:outline-0 block p-2.5 z-[2] max-h-60 overflow-auto"
                                        size={10}
                                    >
                                        <option value="" disabled>Select Property Type</option>
                                        {propertyTypes.map((property: any) => (
                                            <option key={property.type_primary_id} value={property.type_primary_id}>
                                                {property.type_name}
                                            </option>
                                        ))}
                                        {loading && <option disabled>Loading...</option>}
                                    </select>
                                )}
                            </div>
                            {errors.property_type_id && (
                                <p className="text-sm text-red-600 mt-1">{errors.property_type_id}</p>
                            )}
                        </div>

                        {/* Description Name */}
                        <div>
                            <label htmlFor="type_description_name" className="block mb-2 text-sm font-medium text-gray-900">
                                Description Name <span className="text-red-600">*</span>
                            </label>
                            <input
                                type="text"
                                id="type_description_name"
                                name="type_description_name"
                                value={formData.type_description_name}
                                onChange={handleChange}
                                className="bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:outline-0 focus:ring-blue-500 focus:border-blue-500 block w-full p-2.5"
                                placeholder="Enter description name"
                            />
                            {errors.description_name && (
                                <p className="text-sm text-red-600 mt-1">{errors.description_name}</p>
                            )}
                        </div>

                        {/* Submit Button */}
                        <div className="flex items-end">
                            <button
                                type="submit"
                                className="text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 font-medium rounded-lg text-sm w-full px-5 py-2.5 text-center"
                            >
                                Update
                            </button>
                        </div>
                    </div>
                </form>

                {/* Success Message */}
                {successMessage && (
                    <div className="mt-4 p-3 bg-green-600 text-white rounded-lg flex justify-between items-center">
                        <span>{successMessage}</span>
                        <button 
                            type="button"
                            onClick={() => setSuccessMessage('')} 
                            className="text-white font-bold text-lg leading-none"
                        >
                            ×
                        </button>
                    </div>
                )}

                {/* Error Message */}
                {dataExistMessage && (
                    <div className="mt-4 p-3 bg-yellow-600 text-white rounded-lg flex justify-between items-center">
                        <span>{dataExistMessage}</span>
                        <button 
                            type="button"
                            onClick={() => setDataExistMessage('')} 
                            className="text-white font-bold text-lg leading-none"
                        >
                            ×
                        </button>
                    </div>
                )}

                {/* Close Button */}
                <button
                    type="button"
                    onClick={closeModal}
                    className="absolute top-2 right-2 text-gray-500 hover:text-gray-700 text-xl"
                >
                    ×
                </button>
            </div>
        </div>
    );
};

export default PropertyTypeDescriptionEdit;